import React, { useMemo, useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Space, Drawer, Grid, theme } from 'antd';
import {
    UserOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LogoutOutlined,
    ShoppingOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import { CustomerServiceOutlined, QuestionCircleOutlined, TeamOutlined, ShopOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setUserData, userLogout } from '../../redux/actions/DataActions';
import { successMsg } from '../../utils/ShowMessage';
import { routes } from '../../utils/routes.config';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { franchiseService } from '../../services/franchiseService';
import { FaTachometerAlt, FaWarehouse, FaCog, FaCar, FaCode, FaMapMarkerAlt, FaTruckLoading } from "react-icons/fa";
import { BellOutlined } from "@ant-design/icons";
import { HiShoppingCart } from 'react-icons/hi';

const { Header, Sider, Content, Footer } = Layout;
const { useBreakpoint } = Grid;

// Bottom tab bar height (px) — content must clear this on mobile
const BOTTOM_TAB_HEIGHT = 60;

const MainLayout = ({ children }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md; // Screen < 768px
    const [collapsed, setCollapsed] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const userDetail = useSelector((state) => state.UserData.userData);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const isSuperAdmin = userDetail?.role === "superAdmin";
    const isFranchise = userDetail?.role === "franchise";
    const userDbRole = userDetail?.user?.role;
    const isStaff = userDbRole === 'staff';
    const perms = userDetail?.permissions || {};

    const can = (permKey) => {
        if (!isStaff) return true;
        return !!perms?.[permKey];
    };

    const handleLogout = async () => {
        try {
            await ApiServices('post', ApiConfig.LOGOUT, {});
            dispatch(userLogout());
            dispatch(setUserData({}));
            navigate(routes.LOGIN);
            successMsg("Logged out successfully!");
        } catch (error) {
            console.error('Logout error:', error);
            dispatch(userLogout());
            dispatch(setUserData({}));
            navigate(routes.LOGIN);
            successMsg("Logged out successfully!");
        }
    };

    const menuItemsRaw = useMemo(() => {
        if (isFranchise) {
            return [
                { key: routes.FRANCHISE_DASHBOARD, icon: <ShopOutlined style={{ fontSize: 16 }} />, label: 'Dashboard' },
                { key: routes.Orders, icon: <HiShoppingCart size={17} />, label: 'Orders' },
                { key: routes.Drivers, icon: <FaCar size={16} />, label: 'Drivers' },
                { key: routes.LIVE_TRACKING, icon: <FaMapMarkerAlt size={16} />, label: 'Live Tracking' },
                { key: routes.CUSTOM_NOTIFICATIONS, icon: <BellOutlined style={{ fontSize: 16 }} />, label: 'Notifications' },
                { key: routes.SUPPORT, icon: <CustomerServiceOutlined style={{ fontSize: 16 }} />, label: 'Contact & Support' },
            ];
        }
        if (isSuperAdmin) {
            return [
                { key: routes.SUPER_DASHBOARD, icon: <FaTachometerAlt size={16} />, label: 'Dashboard' },
                { key: routes.PLANS, icon: <FaCode size={16} />, label: 'Plans' },
                { key: routes.STAFF, icon: <TeamOutlined style={{ fontSize: 16 }} />, label: 'Staff' },
                { key: routes.SUPPORT, icon: <CustomerServiceOutlined style={{ fontSize: 16 }} />, label: 'Contact & Support' },
                { key: routes.SETTINGS, icon: <FaCog size={16} />, label: 'Setting' },
                { key: routes.CUSTOM_NOTIFICATIONS, icon: <BellOutlined style={{ fontSize: 16 }} />, label: 'Notifications' },
                { key: routes.API_DOCUMENTATION, icon: <FaCode size={16} />, label: 'API Docs' },
                { key: routes.USER_GUIDE, icon: <QuestionCircleOutlined style={{ fontSize: 16 }} />, label: 'User Guide' },
            ];
        }
        return [
            { key: routes.DASHBOARD, icon: <FaTachometerAlt size={16} />, label: 'Dashboard' },
            { key: routes.Orders, icon: <HiShoppingCart size={17} />, label: 'Orders' },
            { key: routes.Drivers, icon: <FaCar size={16} />, label: 'Drivers' },
            { key: routes.LIVE_TRACKING, icon: <FaMapMarkerAlt size={16} />, label: 'Live Tracking' },
            { key: routes.Warehouse, icon: <FaWarehouse size={16} />, label: 'Warehouse' },
            ...(isStaff ? [] : [{ key: routes.STAFF, icon: <TeamOutlined style={{ fontSize: 16 }} />, label: 'Staff' }]),
            ...(isStaff ? [] : [{ key: routes.FRANCHISE, icon: <ShopOutlined style={{ fontSize: 16 }} />, label: 'Franchises' }]),
            { key: routes.SELECT_PLAN, icon: <ShoppingOutlined style={{ fontSize: 16 }} />, label: 'Plans' },
            { key: routes.SUPPORT, icon: <CustomerServiceOutlined style={{ fontSize: 16 }} />, label: 'Contact & Support' },
            { key: routes.SETTINGS, icon: <FaCog size={16} />, label: 'Setting' },
            { key: routes.CUSTOM_NOTIFICATIONS, icon: <BellOutlined style={{ fontSize: 16 }} />, label: 'Notifications' },
            { key: routes.API_DOCUMENTATION, icon: <FaCode size={16} />, label: 'API Docs' },
            { key: routes.USER_GUIDE, icon: <QuestionCircleOutlined style={{ fontSize: 16 }} />, label: 'User Guide' },
        ];
    }, [isSuperAdmin, isStaff, isFranchise]);

    const menuItems = useMemo(() => {
        if (!isStaff) return menuItemsRaw;
        return menuItemsRaw.filter((it) => {
            if (it.key === routes.DASHBOARD) return can('dashboard.read');
            if (it.key === routes.Orders) return can('orders.read');
            if (it.key === routes.Drivers) return can('drivers.read');
            if (it.key === routes.Warehouse) return can('warehouse.read');
            if (it.key === routes.LIVE_TRACKING) return can('live_tracking.read');
            if (it.key === routes.CUSTOM_NOTIFICATIONS) return can('notifications.read');
            if (it.key === routes.SETTINGS) return can('settings.read');
            if (it.key === routes.API_DOCUMENTATION) return can('api_docs.read');
            if (it.key === routes.USER_GUIDE) return true;
            return true;
        });
    }, [isStaff, menuItemsRaw, perms]);

    const [liveFranchise, setLiveFranchise] = useState(null);

    useEffect(() => {
        let isMounted = true;
        if (isFranchise) {
            const fid = userDetail?.franchise?.id || userDetail?.user?.id || userDetail?.id;
            if (fid) {
                franchiseService.getById(fid).then(res => {
                    if (isMounted && res?.success && res.data) {
                        setLiveFranchise(res.data);
                    }
                }).catch(() => { });
            }
        }
        return () => { isMounted = false; };
    }, [isFranchise, userDetail]);

    const activeFranchise = liveFranchise || userDetail?.franchise || (isFranchise ? userDetail?.user : null);
    const franchiseLogo = activeFranchise?.logo || null;
    const franchiseName = activeFranchise?.name || 'Franchise Partner';
    const franchiseSubtitle = activeFranchise?.city ? `${activeFranchise.city} Hub` : 'Franchise Hub';

    const { displayName, displayRole, displayEmail, displayAvatar } = useMemo(() => {
        const u = userDetail?.user || userDetail;

        let name = '';
        let roleLabel = '';
        let email = u?.email || userDetail?.email || '';
        let avatar = null;

        if (isFranchise) {
            name = franchiseName;
            roleLabel = activeFranchise?.city ? `${activeFranchise.city} Franchise` : 'Franchise Partner';
            avatar = franchiseLogo;
            email = activeFranchise?.email || u?.email || '';
        } else if (isSuperAdmin) {
            const first = u?.firstname || '';
            const last = u?.lastname || '';
            name = (first || last) ? `${first} ${last}`.trim() : 'Super Admin';
            roleLabel = 'Super Administrator';
            avatar = u?.image || u?.avatar || null;
        } else if (isStaff) {
            const first = u?.firstname || '';
            const last = u?.lastname || '';
            name = (first || last) ? `${first} ${last}`.trim() : 'Staff Member';
            roleLabel = 'Staff';
            avatar = u?.image || u?.avatar || null;
        } else {
            // Store Owner / Admin
            const first = u?.firstname || '';
            const last = u?.lastname || '';
            name = (first || last) ? `${first} ${last}`.trim() : (u?.name || 'Store Admin');
            roleLabel = userDbRole === 'store_owner' ? 'Store Owner' : 'Store Admin';
            avatar = u?.image || u?.avatar || null;
        }

        return { displayName: name, displayRole: roleLabel, displayEmail: email, displayAvatar: avatar };
    }, [userDetail, isFranchise, isSuperAdmin, isStaff, userDbRole, franchiseName, franchiseLogo, activeFranchise]);

    const userMenuItems = useMemo(() => [
        {
            key: 'user-info',
            label: (
                <div style={{ padding: '4px 2px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>{displayName}</div>
                    {displayEmail && <div style={{ fontSize: 11, color: '#64748b' }}>{displayEmail}</div>}
                    <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 500, marginTop: 2 }}>{displayRole}</div>
                </div>
            ),
            disabled: true,
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined style={{ color: '#ef4444' }} />,
            label: <span style={{ color: '#ef4444', fontWeight: 500 }}>Sign Out</span>,
            onClick: handleLogout,
        },
    ], [displayName, displayEmail, displayRole]);

    const logoContent = (
        <div style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            padding: collapsed && !isMobile ? '0 12px' : '0 18px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
            gap: 12
        }}>
            {isFranchise && franchiseLogo ? (
                <div style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.25)',
                    flexShrink: 0,
                    border: '1.5px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <img
                        src={franchiseLogo}
                        alt={franchiseName}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                </div>
            ) : (
                <div style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 18,
                    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.4)',
                    flexShrink: 0
                }}>
                    {isFranchise ? <ShopOutlined /> : <FaTruckLoading />}
                </div>
            )}
            {(!collapsed || isMobile) && (
                <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 165 }}>
                    <div
                        style={{
                            color: '#f8fafc',
                            fontWeight: 700,
                            fontSize: 15,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={isFranchise ? franchiseName : 'Magecomp'}
                    >
                        {isFranchise ? franchiseName : 'Magecomp'}
                    </div>
                    <div
                        style={{
                            color: isFranchise ? '#93c5fd' : '#64748b',
                            fontSize: 10.5,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginTop: 2
                        }}
                    >
                        {isFranchise ? franchiseSubtitle : 'Delivery Suite'}
                    </div>
                </div>
            )}
        </div>
    );

    const activeSelectedKey = useMemo(() => {
        const currentPath = (location.pathname || '').toLowerCase();
        const found = menuItems.find(
            (item) => item?.key && currentPath === String(item.key).toLowerCase()
        );
        return found ? [found.key] : [location.pathname];
    }, [location.pathname, menuItems]);

    const menuContent = (
        <Menu
            theme="dark"
            mode="inline"
            selectedKeys={activeSelectedKey}
            items={menuItems}
            onClick={({ key }) => {
                navigate(key);
                if (isMobile) setMobileDrawerOpen(false);
            }}
            style={{
                borderRight: 0,
                padding: '8px 0',
                background: 'transparent'
            }}
        />
    );

    const isAuthenticated = !!userDetail?.role;

    // ── Support Contact Info for Platform & Franchise Footer ──────────────────
    const [supportContact, setSupportContact] = useState({
        phone: '+91 79848 02905',
        email: '',
    });

    useEffect(() => {
        let isMounted = true;
        const fetchSupportContact = async () => {
            try {
                const storeId = userDetail?.storeId || userDetail?.user?.storeId || userDetail?.franchise?.storeId;
                const url = storeId ? `${ApiConfig.SUPPORT_CONTACT}?storeId=${storeId}` : ApiConfig.SUPPORT_CONTACT;
                const res = await ApiServices('get', url);
                if (isMounted && res?.data) {
                    setSupportContact({
                        phone: res.data.phone || '+91 79848 02905',
                        email: res.data.email || '',
                    });
                }
            } catch (err) {
                // Silently fallback to defaults
            }
        };
        fetchSupportContact();
        return () => {
            isMounted = false;
        };
    }, [userDetail?.storeId, userDetail?.user?.storeId, userDetail?.franchise?.storeId, userDetail?.role]);

    // Derive current page title for mobile app-bar
    const currentPageTitle = useMemo(() => {
        const found = menuItemsRaw.find(item =>
            item?.key && (location.pathname || '').toLowerCase() === String(item.key).toLowerCase()
        );
        return found?.label || 'Magecomp';
    }, [location.pathname]);

    // ── Bottom Tab Bar (mobile only) ──────────────────────────────────────────
    // Primary tabs: up to 4 route tabs + 1 "More" button that opens the drawer
    const primaryTabRoutes = isSuperAdmin
        ? [routes.SUPER_DASHBOARD, routes.PLANS, routes.STAFF, routes.SUPPORT]
        : isFranchise
            ? [routes.FRANCHISE_DASHBOARD, routes.Orders, routes.Drivers, routes.LIVE_TRACKING]
            : [routes.DASHBOARD, routes.Orders, routes.Drivers, routes.LIVE_TRACKING];

    const primaryTabs = menuItems.filter(item => primaryTabRoutes.includes(item.key));

    // Icon map for bottom bar (larger, simpler icons)
    const tabIconMap = {
        [routes.FRANCHISE_DASHBOARD]: <ShopOutlined style={{ fontSize: 20 }} />,
        [routes.DASHBOARD]: <FaTachometerAlt size={20} />,
        [routes.Orders]: <HiShoppingCart size={20} />,
        [routes.Drivers]: <FaCar size={20} />,
        [routes.LIVE_TRACKING]: <FaMapMarkerAlt size={20} />,
        [routes.SUPER_DASHBOARD]: <FaTachometerAlt size={20} />,
        [routes.PLANS]: <FaCode size={20} />,
        [routes.STAFF]: <TeamOutlined style={{ fontSize: 20 }} />,
        [routes.SUPPORT]: <CustomerServiceOutlined style={{ fontSize: 20 }} />,
    };

    const tabLabelMap = {
        [routes.FRANCHISE_DASHBOARD]: 'Dashboard',
        [routes.DASHBOARD]: 'Dashboard',
        [routes.Orders]: 'Orders',
        [routes.Drivers]: 'Drivers',
        [routes.LIVE_TRACKING]: 'Tracking',
        [routes.SUPER_DASHBOARD]: 'Dashboard',
        [routes.PLANS]: 'Plans',
        [routes.STAFF]: 'Staff',
        [routes.SUPPORT]: 'Support',
    };

    const isTabActive = (key) => {
        const current = (location.pathname || '').toLowerCase();
        return current === String(key).toLowerCase();
    };

    const BottomTabBar = () => (
        <nav style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            background: '#0f172a',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'stretch',
            height: BOTTOM_TAB_HEIGHT,
            // iOS safe area
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.18)',
        }}>
            {primaryTabs.map(tab => {
                const active = isTabActive(tab.key);
                return (
                    <button
                        key={tab.key}
                        onClick={() => navigate(tab.key)}
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            background: active ? 'rgba(37, 99, 235, 0.18)' : 'transparent',
                            border: 'none',
                            borderTop: active ? '2px solid #3b82f6' : '2px solid transparent',
                            cursor: 'pointer',
                            padding: '6px 4px 4px',
                            transition: 'background 0.18s ease',
                            outline: 'none',
                            WebkitTapHighlightColor: 'transparent',
                            minWidth: 0,
                        }}
                    >
                        <span style={{ color: active ? '#60a5fa' : '#64748b', lineHeight: 1, display: 'flex' }}>
                            {tabIconMap[tab.key] || <AppstoreOutlined style={{ fontSize: 20 }} />}
                        </span>
                        <span style={{
                            fontSize: 10,
                            fontWeight: active ? 700 : 500,
                            color: active ? '#93c5fd' : '#64748b',
                            letterSpacing: '0.01em',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}>
                            {tabLabelMap[tab.key] || tab.label}
                        </span>
                    </button>
                );
            })}

            {/* More button — opens full drawer */}
            <button
                onClick={() => setMobileDrawerOpen(true)}
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 3,
                    background: 'transparent',
                    border: 'none',
                    borderTop: '2px solid transparent',
                    cursor: 'pointer',
                    padding: '6px 4px 4px',
                    transition: 'background 0.18s ease',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    minWidth: 0,
                }}
            >
                <span style={{ color: '#64748b', lineHeight: 1, display: 'flex' }}>
                    <AppstoreOutlined style={{ fontSize: 20 }} />
                </span>
                <span style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: '#64748b',
                    letterSpacing: '0.01em',
                }}>
                    More
                </span>
            </button>
        </nav>
    );

    return (
        <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {/* Desktop / Tablet Sider */}
            {!isMobile && isAuthenticated && (
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    width={240}
                    collapsedWidth={76}
                    style={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        height: '100vh',
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: 100,
                        background: '#0f172a',
                        borderRight: '1px solid rgba(255, 255, 255, 0.06)',
                        boxShadow: '2px 0 10px rgba(0, 0, 0, 0.08)'
                    }}
                    theme="dark"
                >
                    {logoContent}
                    {menuContent}
                </Sider>
            )}

            {/* Mobile Navigation Drawer (full menu — all items) */}
            {isMobile && isAuthenticated && (
                <Drawer
                    placement="left"
                    open={mobileDrawerOpen}
                    onClose={() => setMobileDrawerOpen(false)}
                    styles={{ body: { padding: 0, background: '#0f172a' }, header: { display: 'none' } }}
                    width={260}
                >
                    {logoContent}
                    {menuContent}
                </Drawer>
            )}

            <Layout
                style={{
                    marginLeft: !isAuthenticated || isMobile ? 0 : (collapsed ? 76 : 240),
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#f8fafc'
                }}
            >
                <Header
                    style={{
                        padding: isMobile ? '0 16px' : '0 24px',
                        background: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
                        borderBottom: '1px solid #e2e8f0',
                        position: 'sticky',
                        top: 0,
                        zIndex: 90,
                        height: isMobile ? 56 : 64,
                        lineHeight: 1,
                    }}
                >
                    {isAuthenticated ? (
                        isMobile ? (
                            // Mobile app-bar: logo left | page title center | avatar right
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 36 }}>
                                    <div style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 8,
                                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontSize: 15,
                                        flexShrink: 0,
                                    }}>
                                        <FaTruckLoading />
                                    </div>
                                </div>
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontWeight: 700,
                                    fontSize: 16,
                                    color: '#0f172a',
                                    letterSpacing: '-0.02em',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                }}>
                                    {currentPageTitle}
                                </div>
                            </>
                        ) : (
                            <Button
                                type="text"
                                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{
                                    fontSize: '16px',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 8,
                                    color: '#475569',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            />
                        )
                    ) : (
                        <div
                            onClick={() => navigate(routes.HOME)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                        >
                            <div style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: 16,
                                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
                            }}>
                                <FaTruckLoading />
                            </div>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                Magecomp
                            </span>
                        </div>
                    )}

                    {userDetail?.role ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
                            {!isMobile && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', lineHeight: 1.25 }}>
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 13.5,
                                            color: '#0f172a',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 180
                                        }}
                                        title={displayName}
                                    >
                                        {displayName}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginTop: 2 }}>
                                        {displayRole}
                                    </span>
                                </div>
                            )}

                            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
                                <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <Avatar
                                        size={38}
                                        src={displayAvatar || undefined}
                                        icon={!displayAvatar && !displayName ? <UserOutlined /> : undefined}
                                        style={{
                                            background: displayAvatar ? '#fff' : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                            color: '#2563eb',
                                            border: '1.5px solid #bfdbfe',
                                            boxShadow: '0 2px 5px rgba(37, 99, 235, 0.15)',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 15,
                                        }}
                                    >
                                        {!displayAvatar && displayName ? displayName.charAt(0).toUpperCase() : null}
                                    </Avatar>
                                </div>
                            </Dropdown>
                        </div>
                    ) : (
                        <Space size={10}>
                            <Button
                                type="primary"
                                onClick={() => navigate(routes.LOGIN)}
                                style={{ borderRadius: 8, fontWeight: 600 }}
                            >
                                Sign In
                            </Button>
                        </Space>
                    )}
                </Header>

                <Content
                    style={{
                        margin: isMobile ? '12px 12px 0' : '20px 24px 32px',
                        padding: 0,
                        minHeight: 280,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div style={{
                        maxWidth: 1600,
                        width: '100%',
                        margin: '0 auto',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        // Bottom padding inside the content div ensures last card clears the tab bar
                        paddingBottom: isMobile && isAuthenticated
                            ? `calc(${BOTTOM_TAB_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`
                            : 0,
                    }}>
                        {children}
                    </div>
                </Content>

                {/* Platform & Franchise Footer */}
                <Footer
                    style={{
                        textAlign: 'center',
                        background: '#ffffff',
                        borderTop: '1px solid #e2e8f0',
                        padding: isMobile ? '12px 16px' : '14px 20px',
                        marginBottom: isMobile && isAuthenticated ? BOTTOM_TAB_HEIGHT : 0,
                        color: '#64748b',
                        fontSize: isMobile ? 12 : 13,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap', lineHeight: 1.6 }}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>Delivery Operations Platform</span>
                        {supportContact.phone && (
                            <>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <span>
                                    Support:{' '}
                                    <a
                                        href={`tel:${supportContact.phone.replace(/\s+/g, '')}`}
                                        style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                                    >
                                        {supportContact.phone}
                                    </a>
                                </span>
                            </>
                        )}
                        {supportContact.email && (
                            <>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <span>
                                    Email:{' '}
                                    <a
                                        href={`mailto:${supportContact.email}`}
                                        style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                                    >
                                        {supportContact.email}
                                    </a>
                                </span>
                            </>
                        )}
                        <span style={{ color: '#cbd5e1' }}>•</span>
                        <span style={{ color: '#94a3b8', fontSize: isMobile ? 11 : 12 }}>
                            © {new Date().getFullYear()} All rights reserved.
                        </span>
                    </div>
                </Footer>
            </Layout>

            {/* Fixed bottom tab bar — mobile only, authenticated only */}
            {isMobile && isAuthenticated && <BottomTabBar />}
        </Layout>
    );
};

export default MainLayout;
